// external
import React, { FC } from 'react';
import { Typography } from '@mui/material';

// internal
import './coe-thread-card.css';
import { formatThreadAudienceLabels, formatThreadDate } from '../../helpers/general.helpers';
import { palette } from '../../theme';
import { UserAvatar } from '../user-avatar/user-avatar.component';
import { ThreadAudience } from '../../models/thread.model';

export interface TeamGroupOption {
  id: string | number;
  name: string;
}

export interface COEThreadCardProps {
  archived_at?: string;
  date: string;
  creator: string;
  creatorProfileImage?: string | null;
  title: string;
  id: number;
  message?: string;
  updated_at?: string;
  replies?: any[];
  onSendReply?: any;
  onThreadClicked: any;
  onDeleteThread: any;
  viewThread: boolean;
  isCoach: boolean;
  read: boolean;
  isSelected?: boolean;
  audience?: ThreadAudience | null;
  groups?: TeamGroupOption[];
}

const COEThreadCard: FC<COEThreadCardProps> = (props) => {
  const selected = props.isSelected ?? false;
  const isUnread = !props.read;
  const audienceLabels = formatThreadAudienceLabels(props.audience, props.groups);
  const toLine = `To: ${audienceLabels.join(', ')}`;

  const cardStyle: React.CSSProperties = {
    width: '100%',
    backgroundColor: selected ? palette.primary.light : palette.background.default,
    borderLeft: selected ? `3px solid ${palette.primary.main}` : '3px solid transparent',
    borderBottom: `1px solid ${palette.grey[100]}`,
    borderRadius: 0,
    padding: '14px 18px',
    cursor: 'pointer',
    transition: 'background-color 0.12s ease, border-color 0.12s ease',
    boxSizing: 'border-box',
    position: 'relative',
  };

  return (
    <div
      className="thread-card-container"
      style={cardStyle}
      onClick={() => props.onThreadClicked(props.id)}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.backgroundColor = palette.grey[50];
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.backgroundColor = palette.background.default;
      }}
    >
      {/* Row 1: Subject + NEW + date */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
            marginRight: 8,
          }}
        >
          <Typography
            style={{
              fontSize: 15,
              fontWeight: isUnread ? 700 : 600,
              color: palette.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {props.title}
          </Typography>
          {isUnread && (
            <span
              style={{
                backgroundColor: palette.primary.main,
                color: palette.primary.contrastText,
                fontSize: 9,
                fontWeight: 700,
                padding: '2px 6px',
                borderRadius: 6,
                marginLeft: 6,
                verticalAlign: 'middle',
                flexShrink: 0,
              }}
            >
              NEW
            </span>
          )}
        </div>
        <Typography
          style={{
            fontSize: 11,
            color: palette.text.disabled,
            flexShrink: 0,
          }}
        >
          {formatThreadDate(props.date)}
        </Typography>
      </div>

      {/* Row 2: Avatar + from + To */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <UserAvatar src={props.creatorProfileImage} name={props.creator} size={24} fontSize="0.55rem" />
        <Typography
          style={{
            fontSize: 12,
            color: palette.text.secondary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            maxWidth: '40%',
          }}
        >
          {props.creator}
        </Typography>
        <Typography
          style={{
            fontSize: 11,
            color: palette.text.disabled,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {toLine}
        </Typography>
      </div>
    </div>
  );
};

export default COEThreadCard;
